/** Character sheet (P): equipment columns, item level, combat power, stats, sets and sigils. */
import { SIGILS } from '../../game/build';
import { activeSigils, setPieces } from '../../game/character';
import { unequip } from '../../game/inventory';
import { COMBAT_STATS, COMBAT_STAT_NAMES, type EquipItem, type EquipSlot, GEAR_SLOTS, SET_BONUSES, SET_NAMES } from '../../game/items';
import { skillIcon } from '../../ui/icons';
import { openPanel } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import { emptySlotIcon } from './art';
import { type Ctx, ItemCell, Section, commit, hueCss, notify, useCtx } from './common';
import { SLOT_NAME, combatPower, combatStatEffect, equipIlvl, fmt, fmtPct } from './logic';
import { keyTrap } from './keys';
import { sigilIcon } from './sigilArt';
import { QualityBar, tip } from './Tooltip';

const RIGHT_SLOTS = ['necklace', 'earring1', 'earring2', 'ring1', 'ring2', 'stone'] as const;

function ghostFor(slot: EquipSlot, weapon?: string): string {
  const base = slot.replace(/[12]$/, '') as Parameters<typeof emptySlotIcon>[0];
  return emptySlotIcon(base, weapon);
}

function SlotRow({ ctx, slot, side }: { ctx: Ctx; slot: EquipSlot; side: 'l' | 'r' }) {
  const item = (ctx.c.equipment as Record<string, EquipItem | undefined>)[slot];
  const ilvl = item ? equipIlvl(item) : null;
  const doUnequip = () => {
    if (!item) return;
    if (!unequip(ctx.c, slot)) {
      notify('Your bag is full.', '#e5484d');
      return;
    }
    commit({ stats: true });
  };
  return (
    <div class={`bch-slot bch-slot--${side}${item ? '' : ' is-empty'}`}>
      <ItemCell
        item={item}
        size={52}
        weapon={ctx.cls?.weapon}
        ghost={ghostFor(slot, ctx.cls?.weapon)}
        label={item ? `${item.name} — click to unequip` : `${SLOT_NAME[slot]} (empty)`}
        tipProps={{ where: 'equipped', hints: ['Click to unequip'] }}
        onClick={doUnequip}
      />
      <div class="bch-slot-text">
        <div class="bch-slot-label">{SLOT_NAME[slot]}</div>
        {item ? (
          <>
            <div class={`bch-slot-name r-${item.rarity}`}>
              {item.kind === 'gear' && item.plus > 0 ? <span class="bch-plus">+{item.plus} </span> : null}
              {item.name}
            </div>
            <div class="bch-slot-meta">
              {ilvl !== null ? <span>iLvl {fmt(ilvl)}</span> : <span>{item.kind === 'stone' ? `${Math.round(item.chance * 100)}% etch` : ''}</span>}
              {item.kind !== 'stone' && <QualityBar q={item.quality} label={false} thin />}
            </div>
          </>
        ) : (
          <div class="bch-slot-empty">Empty</div>
        )}
      </div>
    </div>
  );
}

export function CharacterPanel() {
  const ctx = useCtx();
  if (!ctx) return null;
  const { p, c, cls } = ctx;
  const built = p.build();
  const s = built.summary;
  const b = built.base;
  const sets = Object.entries(setPieces(c));
  const sigils = activeSigils(c);
  const apex = cls?.awakenings.find((a) => a.id === c.awakening) ?? cls?.awakenings[0];
  return (
    <Window title="Character" width={1000} height={720} className="bwin bwin--char">
      <div class="bch" {...keyTrap}>
        <header class="bch-head">
          <div class="bch-crest" style={{ '--hue': hueCss(cls?.hue ?? 0x8888aa) }}>
            {apex && <img src={skillIcon(apex, cls?.hue)} alt="" />}
          </div>
          <div class="bch-id">
            <div class="bch-name">{c.name}</div>
            <div class="bch-sub">
              <span class="bch-lv">Lv. {c.level}</span>
              <span class="bch-cls">{cls?.name ?? c.classId}</span>
              {cls && <span class="bch-title">{cls.title}</span>}
            </div>
          </div>
          <div class="bch-legacy">
            <span>Legacy Rank</span>
            <b>{p.legacyRank}</b>
          </div>
        </header>
        <div class="bch-body">
          <div class="bch-col">
            <div class="bch-slots">
              {GEAR_SLOTS.map((slot) => (
                <SlotRow key={slot} ctx={ctx} slot={slot} side="l" />
              ))}
            </div>
            <Section title="Set Bonuses" class="bch-sets">
              {sets.length === 0 ? (
                <p class="bch-empty">No set pieces equipped. Set gear comes from Deepvault and the Warlord Siege.</p>
              ) : (
                sets.map(([id, n]) => (
                  <div key={id} class="bch-set">
                    <div class="bch-set-h">
                      {SET_NAMES[id] ?? id} <span>{n} / 6</span>
                    </div>
                    {(SET_BONUSES[id] ?? []).map((bn) => (
                      <div key={bn.pieces} class={`bch-set-l ${n >= bn.pieces ? 'is-on' : ''}`}>
                        <span>{bn.pieces}</span>
                        {bn.text}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </Section>
          </div>

          <div class="bch-mid">
            <div class="bch-ilvl" {...tip(() => <div class="btt-note">Average item level of your six gear pieces, including tempering. Empty slots count as 0.</div>)}>
              <div class="bch-ilvl-label">Equipped Item Level</div>
              <div class="bch-ilvl-value">{built.ilvl.toFixed(2)}</div>
            </div>
            <div class="bch-cp" {...tip(() => <div class="btt-note">A summary of offence and survivability. Raise it with tempering, accessories, sigils, gems and cards.</div>)}>
              <span>Combat Power</span>
              <b>{fmt(combatPower(built))}</b>
            </div>
            <Section title="Basic" class="bch-basic">
              <div class="bch-kv">
                <span>Attack Power</span>
                <b>{fmt(s.attack)}</b>
              </div>
              <div class="bch-kv">
                <span>Max HP</span>
                <b>{fmt(s.maxHp)}</b>
              </div>
            </Section>
            <Section title="Combat" class="bch-combat">
              <div class="bch-stats">
                {COMBAT_STATS.map((st) => (
                  <div key={st} class={`bch-stat ${built.combat[st] > 0 ? '' : 'is-zero'}`} {...tip(() => <div class="btt-note">{combatStatEffect(st, built.combat[st], true)}</div>)}>
                    <span class="bch-stat-n">{COMBAT_STAT_NAMES[st]}</span>
                    <b>{fmt(built.combat[st])}</b>
                    <span class="bch-stat-e">{combatStatEffect(st, built.combat[st])}</span>
                  </div>
                ))}
              </div>
            </Section>
            <div class="bch-derived">
              <span>
                Crit Rate <b>{fmtPct(s.critRate)}</b>
              </span>
              <span>
                Crit Damage <b>{fmtPct(s.critDmg, 0)}</b>
              </span>
              <span>
                Attack Speed <b>{fmtPct(s.atkSpeed, 0)}</b>
              </span>
              <span>
                Move Speed <b>{s.moveSpeed.toFixed(1)} m/s</b>
              </span>
              <span>
                Cooldowns <b>−{fmtPct(s.cdr)}</b>
              </span>
              <span>
                Damage Bonus <b>+{fmtPct(b.dmgBonus)}</b>
              </span>
            </div>
          </div>

          <div class="bch-col bch-col--r">
            <div class="bch-slots">
              {RIGHT_SLOTS.map((slot) => (
                <SlotRow key={slot} ctx={ctx} slot={slot} side="r" />
              ))}
            </div>
            <Section
              title="Active Sigils"
              class="bch-sigils"
              right={
                <button type="button" class="blink" onClick={() => openPanel('sigils')}>
                  Manage
                </button>
              }
            >
              {sigils.length === 0 ? (
                <p class="bch-empty">No active sigils. Learn them from codices and accessories, then choose up to five.</p>
              ) : (
                <div class="bch-sig-list">
                  {sigils.map((sg) => {
                    const def = SIGILS[sg.id];
                    return (
                      <div
                        key={sg.id}
                        class={`bch-sig ${def?.negative ? 'is-neg' : ''}`}
                        {...tip(() => (
                          <div class="btt-note">
                            <b class={def?.negative ? 'btt-neg' : 'btt-sigil'}>{def?.name}</b> — Lv. {sg.level}
                            <br />
                            {def?.levels[sg.level - 1]}
                          </div>
                        ))}
                      >
                        <img src={sigilIcon(sg.id)} alt="" />
                        <span>{def?.name ?? sg.id}</span>
                        <b>Lv.{sg.level}</b>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>
          </div>
        </div>
      </div>
    </Window>
  );
}
